import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Sparkles, Loader2, Coins, Wrench, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

/**
 * Admin · Catálogo de extras
 *
 * Lets the admin set, per extra service:
 *   • a fixed €€€ price, OR
 *   • flag "a combinar" (price_on_request) → the booking total ignores
 *     the extra and the admin agrees a price with the client when
 *     invoicing.
 *
 * Each row is independently editable and persisted via PUT
 * /api/admin/catalog/extras/{eid}. Reset returns the extra to its
 * hardcoded default (POST /reset).
 */
export default function AdminExtras() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [resettingId, setResettingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/catalog/extras");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  function update(eid, patch) {
    setItems(curr => curr.map(it => it.id === eid ? { ...it, ...patch } : it));
  }

  async function save(eid) {
    const it = items.find(i => i.id === eid);
    if (!it) return;
    setSavingId(eid);
    try {
      await api.put(`/admin/catalog/extras/${eid}`, {
        name: it.name,
        price: Number(it.price) || 0,
        price_on_request: !!it.price_on_request,
        enabled: it.enabled !== false,
      });
      toast.success(`«${it.name}» atualizado.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  }

  async function reset(eid) {
    const it = items.find(i => i.id === eid);
    if (!it) return;
    if (!window.confirm(`Repor «${it.name}» para o valor original do sistema?`)) return;
    setResettingId(eid);
    try {
      await api.post(`/admin/catalog/extras/${eid}/reset`);
      toast.success(`«${it.name}» reposto.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResettingId(null);
    }
  }

  async function toggleEnabled(eid) {
    const it = items.find(i => i.id === eid);
    if (!it) return;
    const next = !(it.enabled !== false);
    try {
      await api.put(`/admin/catalog/extras/${eid}`, { enabled: next });
      toast.success(next
        ? `«${it.name}» visível para clientes.`
        : `«${it.name}» oculto dos clientes.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-extras-page">
      <header>
        <h1 className="font-display text-3xl text-[#fdfbf7]">Catálogo de extras</h1>
        <p className="text-sm text-[#a0b2af] mt-1 max-w-2xl">
          Defina o preço fixo de cada extra ou marque-o como
          <strong className="text-[#d4af37]"> «a combinar»</strong> para acertar
          o valor com o cliente caso a caso (não soma ao total da reserva).
        </p>
      </header>

      {loading ? (
        <div className="text-[#a0b2af]">A carregar…</div>
      ) : (
        <div className="space-y-3">
          {items.map(it => {
            const isEnabled = it.enabled !== false;
            return (
            <article key={it.id}
                     className={`lux-card p-5 grid gap-4 sm:grid-cols-[1fr_auto_auto_auto_auto] items-end ${isEnabled ? "" : "opacity-60"}`}
                     data-testid={`extra-row-${it.id}`}>

              {/* Name + id */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] flex items-center gap-2">
                  ID · {it.id}
                  {!isEnabled && (
                    <span className="px-1.5 py-0.5 rounded-sm bg-[#1a4640] text-[#fcd34d] text-[9px] tracking-widest"
                          data-testid={`extra-disabled-badge-${it.id}`}>
                      OCULTO
                    </span>
                  )}
                </div>
                <div className="font-display text-lg text-[#fdfbf7] mt-0.5">
                  {it.name}
                </div>
                {it.requires_description && (
                  <div className="text-[11px] text-[#a0b2af] mt-1">
                    O cliente é obrigado a descrever o serviço pretendido.
                  </div>
                )}
              </div>

              {/* Visibility toggle button */}
              <button onClick={() => toggleEnabled(it.id)}
                      title={isEnabled ? "Desativar (esconder dos clientes)" : "Ativar (mostrar aos clientes)"}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-semibold uppercase tracking-widest transition-colors ${isEnabled
                        ? "border-2 border-[#1a4640] text-[#a0b2af] hover:border-[#f59e0b]/50 hover:text-[#f59e0b]"
                        : "border-2 border-[#10b981]/60 text-[#10b981] bg-[#10b981]/10 hover:bg-[#10b981]/20"}`}
                      data-testid={`extra-visibility-${it.id}`}>
                {isEnabled
                  ? <><Eye size={12} /> Visível</>
                  : <><EyeOff size={12} /> Ativar</>}
              </button>

              {/* Mode toggle */}
              <label className="flex items-center gap-2 text-xs text-[#fdfbf7] bg-[#041f1b] border border-[#1a4640] rounded-sm px-3 py-2 cursor-pointer"
                     data-testid={`extra-on-request-${it.id}`}>
                <input type="checkbox"
                       checked={!!it.price_on_request}
                       onChange={e => update(it.id, { price_on_request: e.target.checked })}
                       className="accent-[#d4af37]" />
                <Wrench size={12} className="text-[#d4af37]" />
                A combinar
              </label>

              {/* Price input — disabled when on-request */}
              <label className={`flex items-center gap-2 bg-[#041f1b] border border-[#1a4640] rounded-sm px-3 py-2 ${it.price_on_request ? "opacity-40" : ""}`}>
                <Coins size={14} className="text-[#d4af37]" />
                <input type="number" min="0" step="0.5"
                       value={it.price ?? 0}
                       disabled={!!it.price_on_request}
                       onChange={e => update(it.id, { price: e.target.value })}
                       className="bg-transparent w-20 text-right text-sm text-[#fdfbf7] outline-none disabled:cursor-not-allowed"
                       data-testid={`extra-price-${it.id}`} />
                <span className="text-xs text-[#a0b2af]">€</span>
              </label>

              {/* Actions */}
              <div className="flex gap-2 justify-end">
                <button onClick={() => reset(it.id)} disabled={resettingId === it.id}
                        title="Repor valor original"
                        className="btn-ghost-gold px-3 py-2 text-xs disabled:opacity-50"
                        data-testid={`extra-reset-${it.id}`}>
                  {resettingId === it.id
                    ? <Loader2 size={14} className="animate-spin" />
                    : <RotateCcw size={14} />}
                </button>
                <button onClick={() => save(it.id)} disabled={savingId === it.id}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm text-xs font-semibold text-[#031411] bg-[#d4af37] hover:bg-[#f5d76e] transition-colors disabled:opacity-60"
                        data-testid={`extra-save-${it.id}`}>
                  {savingId === it.id
                    ? <><Loader2 size={12} className="animate-spin" /> A guardar…</>
                    : <><Save size={12} /> Guardar</>}
                </button>
              </div>
            </article>
            );
          })}
        </div>
      )}

      <aside className="lux-card p-5 border-[#d4af37]/30 bg-gradient-to-br from-[#062c27] to-[#031411]">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-full bg-[#d4af37]/15 grid place-items-center text-[#d4af37] shrink-0">
            <Sparkles size={16} />
          </span>
          <div className="text-xs text-[#a0b2af] leading-relaxed">
            Os preços aqui definidos refletem-se imediatamente em todo o
            sistema: cálculo de preço da reserva, página pública de
            preçário, recibos e faturas. Extras marcados como
            <strong className="text-[#d4af37]"> «a combinar»</strong> não
            somam ao total da reserva — o admin acerta o valor final
            antes de faturar.
          </div>
        </div>
      </aside>
    </div>
  );
}
